/**
 * Tailwind-styled text input. Renders the label, the input, an optional
 * error message and an optional hint. Forwards every standard <input> prop.
 *
 * The error and hint are PROGRAMMATICALLY ASSOCIATED with the input via
 * `aria-describedby`, and an error also sets `aria-invalid`. Before this,
 * the error was a bare `<p>` sitting next to the field: sighted users saw
 * it, screen-reader users navigating by form control heard the label and
 * nothing else, so the field was announced as valid with no explanation of
 * why submit had failed. `role="alert"` additionally announces the message
 * the moment it appears, which is what a validation failure needs.
 *
 * A caller-supplied `aria-describedby` is preserved and appended to rather
 * than overwritten, so a field can carry extra description without losing
 * its error.
 */

import { forwardRef } from 'react'
import type { InputHTMLAttributes } from 'react'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  /** Persistent helper text, shown when there is no error. */
  hint?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, id, className = '', required, ...rest },
  ref,
) {
  const errorId = error && id ? `${id}-error` : undefined
  const hintId = hint && !error && id ? `${id}-hint` : undefined
  const describedBy =
    [rest['aria-describedby'], errorId, hintId].filter(Boolean).join(' ') || undefined
  const baseInputClasses =
    'block w-full rounded-md border border-border-strong bg-bg-elevated px-3 py-2 text-sm text-fg placeholder:text-fg-subtle shadow-sm focus:border-fg focus:outline-none focus:ring-1 focus:ring-fg disabled:bg-bg-muted disabled:cursor-not-allowed'
  const errorInputClasses = error ? 'border-danger focus:border-danger focus:ring-danger' : ''
  const inputClasses = `${baseInputClasses} ${errorInputClasses} ${className}`.trim()

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-fg-muted">
          {label}
          {required && (
            <span aria-hidden="true" className="ms-0.5 text-danger">
              *
            </span>
          )}
        </label>
      )}
      <input
        ref={ref}
        id={id}
        required={required}
        className={inputClasses}
        {...rest}
        aria-invalid={error ? true : rest['aria-invalid']}
        aria-describedby={describedBy}
      />
      {error && (
        <p id={errorId} role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
      {hint && !error && (
        <p id={hintId} className="text-xs text-fg-muted">
          {hint}
        </p>
      )}
    </div>
  )
})
